import { ChargeSource, DiscountMode, FolioEventKind, FolioEventStatus, FolioStatus, PaymentMethod } from '@/enums/folio'
import i18n from '@/libs/i18n'
import type { SelectOption, TagItem } from '@/types/Common'
import type { ICharge, IChargeRow, IFolio, IFolioEvent, IPayment, IPaymentRow } from '@/types/Folio'

/** A Go zero ObjectID. It means "not set", never "the record with this id". */
export const ZERO_ID = '000000000000000000000000'

export const isZeroId = (id?: string | null): boolean => !id || id === ZERO_ID

export const isFolioOpen = (folio?: IFolio | null): boolean => folio?.status === FolioStatus.Open

export const getFolioStatus = (status: string): TagItem => {
    const map: Record<string, TagItem> = {
        [FolioStatus.Open]:   { label: i18n.global.t('folio.status_open'),   color: 'success' },
        [FolioStatus.Closed]: { label: i18n.global.t('folio.status_closed'), color: 'neutral' },
    }

    return map[status] ?? { label: status, color: 'neutral' }
}

export const chargeSourceLabel = (source: string): string => {
    const key = `charge.source_${source}`
    const label = i18n.global.t(key)

    return label === key ? source : label
}

/**
 * The sources the desk may post by hand.
 *
 * Room is missing on purpose and not merely hidden: Logic.PostCharge refuses it, so
 * the price of a stay can only reach a folio through the room-charge route, which is
 * the one that may not post twice.
 */
export const postableChargeSources = (): SelectOption[] =>
    [ChargeSource.Service, ChargeSource.POS, ChargeSource.TouristTax, ChargeSource.Manual]
        .map(value => ({ label: chargeSourceLabel(value), value }))

export const paymentMethodLabel = (method: string): string => {
    const key = `payment.method_${method}`
    const label = i18n.global.t(key)

    return label === key ? method : label
}

export const paymentMethodOptions = (): SelectOption[] =>
    Object.values(PaymentMethod).map(value => ({ label: paymentMethodLabel(value), value }))

/**
 * A VAT rate is basis points on the wire (2200 = 22 %) because it is multiplied into
 * money. It is divided only here, for the eye, and the result never travels back.
 */
export const formatTaxRate = (basisPoints?: number | null): string =>
    new Intl.NumberFormat(i18n.global.locale.value, {
        style:                 'percent',
        maximumFractionDigits: 2,
    }).format((basisPoints ?? 0) / 10000)

/** The percentage a form field shows, from the basis points the API speaks. */
export const fromBasisPoints = (basisPoints?: number | null): number => (basisPoints ?? 0) / 100

/** And back. Rounds, so 9.505 becomes 951 basis points rather than 950. */
export const toBasisPoints = (percentage?: number | null): number => Math.round((percentage ?? 0) * 100)

/**
 * What the guest still owes, in cents.
 *
 * The server deliberately does not compute this: charges and payments are two
 * domains and it will not quote a number assembled from both. So it is subtracted
 * here — as integers, from the two totals the server did compute, never by re-summing
 * either column and never through a decimal.
 */
export const folioBalance = (chargesTotal?: number | null, paymentsTotal?: number | null): number =>
    (chargesTotal ?? 0) - (paymentsTotal ?? 0)

/**
 * How the balance reads at a glance. Zero is the one the desk is working towards, so
 * it is the only one that is quiet; an overpayment is not an error but it is money
 * that has to be given back, so it is called out rather than shown as a minus sign.
 */
export const getBalanceStatus = (balance: number): TagItem => {
    if (balance > 0) return { label: i18n.global.t('folio.balance_due'),      color: 'warning' }
    if (balance < 0) return { label: i18n.global.t('folio.balance_overpaid'), color: 'info' }

    return { label: i18n.global.t('folio.balance_settled'), color: 'success' }
}

/**
 * Orders the lines so that a storno sits directly under the line it cancels, and
 * numbers what it prints.
 *
 * The numbering is the point. A reversal carries a negative amount, and a negative
 * amount alone does not say which line it undoes — on a folio with three identical
 * bar charges it says nothing at all. So each printed line gets the number the screen
 * shows it under, and both halves of a pair name the other half by that number. The
 * pair is then readable without colour, without the sign, and without the two rows
 * having to stay adjacent when somebody sorts differently.
 *
 * Both directions are used to find a pair, not just the forward one: the reversal
 * always points back at its original, while the original's forward pointer is written
 * in a second step that a crash can leave undone (see Logic.ReverseCharge). Deriving
 * "this line was stornoed" from the reversals that name it is therefore the half that
 * cannot be missing.
 */
export const orderCharges = (charges: ICharge[]): IChargeRow[] => {
    const reversalsOf = new Map<string, ICharge[]>()
    const originals: ICharge[] = []

    charges.forEach(charge => {
        if (isZeroId(charge.reverses_charge_id)) {
            originals.push(charge)

            return
        }

        const list = reversalsOf.get(charge.reverses_charge_id) ?? []

        list.push(charge)
        reversalsOf.set(charge.reverses_charge_id, list)
    })

    // The discounts of a line sit under it too, and BEFORE its storno: they came off
    // the sale, and a storno cancels what was left. Read from the discount's own
    // pointer, never from a forward one — the original never gets marked, because a
    // discounted line is still a live posting of whatever it was.
    const discountsOf = new Map<string, ICharge[]>()

    originals.forEach(charge => {
        if (isZeroId(charge.discount_of)) return

        const list = discountsOf.get(charge.discount_of) ?? []

        list.push(charge)
        discountsOf.set(charge.discount_of, list)
    })

    const ordered: ICharge[] = []

    originals.forEach(charge => {
        if (!isZeroId(charge.discount_of)) return

        ordered.push(charge)
        ;(discountsOf.get(charge.id) ?? []).forEach(discount => {
            ordered.push(discount)
            ;(reversalsOf.get(discount.id) ?? []).forEach(reversal => ordered.push(reversal))
            reversalsOf.delete(discount.id)
        })
        discountsOf.delete(charge.id)
        ;(reversalsOf.get(charge.id) ?? []).forEach(reversal => ordered.push(reversal))
        reversalsOf.delete(charge.id)
    })

    // A discount whose original is not on this card, or a storno whose original is
    // not — neither should exist. If one ever appears it is printed rather than
    // dropped: a missing line is a folio that does not add up.
    discountsOf.forEach(list => list.forEach(discount => ordered.push(discount)))
    reversalsOf.forEach(list => list.forEach(reversal => ordered.push(reversal)))

    const lineOf = new Map<string, number>()

    ordered.forEach((charge, index) => lineOf.set(charge.id, index + 1))

    return ordered.map((charge, index) => {
        const reversals = ordered.filter(item => item.reverses_charge_id === charge.id)
        const isReversal = !isZeroId(charge.reverses_charge_id)

        const pairs = isReversal
            ? [lineOf.get(charge.reverses_charge_id)]
            : reversals.map(item => lineOf.get(item.id))

        const discounts = ordered.filter(item => item.discount_of === charge.id)
        const isDiscount = !isZeroId(charge.discount_of)

        return {
            charge,
            line:        index + 1,
            pair_lines:  pairs.filter((line): line is number => !!line),
            is_reversal: isReversal,
            is_reversed: reversals.length > 0 || !isZeroId(charge.reversed_by_charge_id),

            is_discount:      isDiscount,
            discount_lines:   discounts.map(item => lineOf.get(item.id)).filter((line): line is number => !!line),
            discount_of_line: isDiscount ? lineOf.get(charge.discount_of) ?? 0 : 0,
        }
    })
}

/**
 * The same for the settlement list. A refund is the payment-side storno and reads the
 * same way — except that one payment may be refunded in several parts, so an original
 * can name more than one counterpart.
 */
export const orderPayments = (payments: IPayment[]): IPaymentRow[] => {
    const refundsOf = new Map<string, IPayment[]>()
    const originals: IPayment[] = []

    payments.forEach(payment => {
        if (isZeroId(payment.refunds_payment_id)) {
            originals.push(payment)

            return
        }

        const list = refundsOf.get(payment.refunds_payment_id) ?? []

        list.push(payment)
        refundsOf.set(payment.refunds_payment_id, list)
    })

    const ordered: IPayment[] = []

    originals.forEach(payment => {
        ordered.push(payment)
        ;(refundsOf.get(payment.id) ?? []).forEach(refund => ordered.push(refund))
        refundsOf.delete(payment.id)
    })

    refundsOf.forEach(list => list.forEach(refund => ordered.push(refund)))

    const lineOf = new Map<string, number>()

    ordered.forEach((payment, index) => lineOf.set(payment.id, index + 1))

    return ordered.map((payment, index) => {
        const refunds = ordered.filter(item => item.refunds_payment_id === payment.id)
        const isRefund = !isZeroId(payment.refunds_payment_id)

        const pairs = isRefund
            ? [lineOf.get(payment.refunds_payment_id)]
            : refunds.map(item => lineOf.get(item.id))

        return {
            payment,
            line:        index + 1,
            pair_lines:  pairs.filter((line): line is number => !!line),
            is_refund:   isRefund,
            is_refunded: refunds.length > 0,
        }
    })
}

/**
 * What may still be discounted or taken off: a live line that is not itself a
 * reduction of another one.
 *
 * A discount line is refused by the server — its amount is negative, and a percentage
 * of a negative is money ADDED to a bill — and a line that is half of a storno pair is
 * already a correction record. The buttons are simply not offered where the request
 * would come back as a conflict.
 */
export const isReducible = (row: IChargeRow): boolean =>
    !row.is_reversal && !row.is_reversed && !row.is_discount

/**
 * The accommodation may be discounted and may NOT be removed: the guest slept the
 * nights, so paying less for them is a decision and pretending they did not happen is
 * not. The server refuses it (InternalErrors.RoomNightLineNotRemovable) and the screen
 * says so rather than offering a button that always fails.
 */
export const isRemovable = (row: IChargeRow): boolean =>
    isReducible(row) && row.charge.source !== ChargeSource.Room

/**
 * A line may be moved to another card only while it is not part of a storno pair.
 * The server refuses the rest — splitting an original from its reversal across two
 * folios leaves both cards wrong and both looking internally consistent — so the
 * button is not offered where the request would come back as a conflict.
 */
export const isMovable = (row: IChargeRow): boolean => !row.is_reversal && !row.is_reversed

/** A storno is not itself reversible, and a line is stornoed once. */
export const isReversible = (row: IChargeRow): boolean => !row.is_reversal && !row.is_reversed

/** A refund reverses a payment; reversing a refund would be a payment. */
export const isRefundable = (row: IPaymentRow): boolean => !row.is_refund && row.payment.amount > 0

/** What a card is called when it has no label yet — the desk still has to pick one. */
export const folioName = (folio: IFolio): string =>
    folio.label || `${i18n.global.t('folio.folio')} ${folio.id.slice(-6)}`

/**
 * A change to a bill, as it reads at a glance.
 *
 * Pending is a warning rather than an error: nothing has gone wrong, somebody simply
 * has to look at it. Rejected is neutral and not an error for the same reason — a
 * manager saying no is the system working.
 */
export const getFolioEventStatus = (status: string): TagItem => {
    const map: Record<string, TagItem> = {
        [FolioEventStatus.PendingApproval]: { label: i18n.global.t('bill_change.status_pending'),  color: 'warning' },
        [FolioEventStatus.Applied]:         { label: i18n.global.t('bill_change.status_applied'),  color: 'success' },
        [FolioEventStatus.Rejected]:        { label: i18n.global.t('bill_change.status_rejected'), color: 'neutral' },
    }

    return map[status] ?? { label: status, color: 'neutral' }
}

export const folioEventKindLabel = (kind: string): string => {
    const map: Record<string, string> = {
        [FolioEventKind.Discount]: i18n.global.t('bill_change.kind_discount'),
        [FolioEventKind.Removal]:  i18n.global.t('bill_change.kind_removal'),
    }

    return map[kind] ?? kind
}

export const discountModeOptions = (): SelectOption[] => [
    { label: i18n.global.t('bill_change.mode_percent'), value: DiscountMode.Percent },
    { label: i18n.global.t('bill_change.mode_amount'),  value: DiscountMode.Amount },
]

/**
 * What a discount comes to against the line, in basis points, rounded UP — the same
 * arithmetic Logic.effectiveBasisPoints does, and it has to stay the same to the point:
 * this is what tells the desk BEFORE it presses whether the figure it typed will apply
 * at once or go to a manager, and a screen that rounded down would promise one thing
 * and produce the other.
 */
export const effectiveBasisPoints = (lineAmount: number, discount: number): number => {
    if (lineAmount <= 0) return 10000

    return Math.ceil((discount * 10000) / lineAmount)
}

/**
 * What a percentage takes off a line, in cents, TRUNCATED.
 *
 * Truncated because the server truncates: the remainder stays on the guest's bill, so
 * the house never gives away a cent it was not asked to. A preview that rounded to the
 * nearest would show a figure one cent away from the one the guest is charged.
 */
export const discountFromBasisPoints = (lineAmount: number, basisPoints: number): number =>
    Math.trunc((lineAmount * basisPoints) / 10000)

/**
 * A durable name for one press of a dialog: minted when it opens, resent unchanged on
 * a retry, so the same press never takes money off a bill twice (CLAUDE.md invariant
 * 5). The same shape the transfer dialog already uses.
 */
export const newOperationId = (): string =>
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

/** Whether this account's own ceiling covers a waiting request. */
export const canDecide = (event: IFolioEvent, limitBasisPoints: number): boolean =>
    limitBasisPoints > 0 && event.effective_basis_points <= limitBasisPoints
